import { ConfigResponse } from './../api/data/ConfigResponse'
import { RegionResponse } from './../api/data/RegionResponse'
import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { ReservationRequest } from '../api/data/ReservationRequest'
import type { StepItemData } from '../models/StepItemList'
import stepItemList from '../models/StepItemList'
import { AvailableVehicle } from '../api/data/AvailableVehicle'
import { ConfigVehicleData } from '../api/data/ConfigResponse'
import { AddOnResponse } from '../api/data/AddOnResponse'
import type { ReservationResponse } from '../api/data/ReservationResponse'
import type { RezResponse } from '../api/data/RezResponse'
import { LocationExtended } from '../api/data/LocationExtended'

type State = {
  reservation: undefined | ReservationRequest
  associatedVehicles: undefined | AvailableVehicle
  configVehicleData: undefined | ConfigVehicleData[]
  rezCurrentStep: undefined | StepItemData
  loading: boolean
  sessionToken: string
  regionsData: undefined | RegionResponse[]
  addOnData: undefined | AddOnResponse[]
  reservationResponse: undefined | RezResponse<ReservationResponse>
  locationData: undefined | LocationExtended
  configAction: undefined | ConfigResponse[]
  rezAdvancedToStep: number
  stellantisParentNotifyCustomerInfoClick: boolean
  stellantisParentNotifySubmitClick: boolean
}

const initialState: State = {
  reservation: {} as ReservationRequest,
  associatedVehicles: {} as AvailableVehicle,
  configVehicleData: {} as ConfigVehicleData[],
  rezCurrentStep: stepItemList[0],
  loading: false,
  sessionToken: '',
  regionsData: undefined,
  addOnData: undefined,
  reservationResponse: undefined,
  locationData: undefined,
  configAction: undefined,
  rezAdvancedToStep: 0,
  stellantisParentNotifyCustomerInfoClick: false,
  stellantisParentNotifySubmitClick: false
}

export const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    selectReservationDatesAction: (state, action: PayloadAction<ReservationRequest | undefined>) => {
      state.reservation = action.payload ?? {}
      state.rezCurrentStep = stepItemList[0]
      state.loading = true
    },
    selectVehicleAction: (state, action: PayloadAction<ReservationRequest | undefined>) => {
      state.reservation = action.payload
      state.rezCurrentStep = stepItemList[1]
      state.loading = true
    },
    enterCustomerInfoAction: (state, action: PayloadAction<ReservationRequest | undefined>) => {
      state.reservation = action.payload
      state.rezCurrentStep = stepItemList[2]
      state.loading = true
    },
    enterPaymentInfoAction: (state, action: PayloadAction<ReservationRequest | undefined>) => {
      state.reservation = action.payload
      state.rezCurrentStep = stepItemList[3]
      state.loading = true
    },
    reviewReservationAction: (state, action: PayloadAction<ReservationRequest | undefined>) => {
      state.reservation = action.payload
      state.rezCurrentStep = stepItemList[4]
      state.loading = true
    },
    confirmReservationAction: (state, action: PayloadAction<RezResponse<ReservationResponse> | undefined>) => {
      state.reservationResponse = action.payload
      state.rezCurrentStep = stepItemList[5]
      state.loading = true
    },
    clearSavedReservationAction: (state, action: PayloadAction<boolean | undefined>) => {
      state.reservation = {} as ReservationRequest
      state.rezCurrentStep = action.payload ? stepItemList[5] : stepItemList[1]
    },
    updateReservationAction: (state, action: PayloadAction<ReservationRequest | undefined>) => {
      state.reservation = action.payload
    },
    setApiKeyAction: (state, action: PayloadAction<string>) => {
      state.sessionToken = action.payload
    },
    associatedVehiclesAction: (state, action: PayloadAction<AvailableVehicle | undefined>) => {
      state.associatedVehicles = action.payload
    },
    configVehicleDataAction: (state, action: PayloadAction<ConfigVehicleData[] | undefined>) => {
      state.configVehicleData = action.payload
    },
    getRegionsAction: (state, action: PayloadAction<RegionResponse[] | undefined>) => {
      state.regionsData = action.payload
    },
    setLocationData: (state, action: PayloadAction<LocationExtended | undefined>) => {
      state.locationData = action.payload
    },
    configAction: (state, action: PayloadAction<ConfigResponse[] | undefined>) => {
      state.configAction = action.payload
    },
    rezAdvancedToStep: (state, action: PayloadAction<number>) => {
      state.rezAdvancedToStep = action.payload
    },
    setStellantisParentNotifySubmitClick: (state, action: PayloadAction<boolean>) => {
      state.stellantisParentNotifySubmitClick = action.payload
    },
    setStellantisParentNotifyCustomerInfoClick: (state, action: PayloadAction<boolean>) => {
      state.stellantisParentNotifyCustomerInfoClick = action.payload
    }
  }
})

export const {
  selectReservationDatesAction,
  selectVehicleAction,
  enterCustomerInfoAction,
  enterPaymentInfoAction,
  reviewReservationAction,
  confirmReservationAction,
  updateReservationAction,
  setApiKeyAction,
  associatedVehiclesAction,
  configVehicleDataAction,
  getRegionsAction,
  clearSavedReservationAction,
  setLocationData,
  configAction,
  rezAdvancedToStep,
  setStellantisParentNotifyCustomerInfoClick,
  setStellantisParentNotifySubmitClick
} = reservationSlice.actions

export default reservationSlice.reducer
